import { Task, TaskHistoryEntry } from '../../../types';

const DEFAULT_FIELDS_TO_LOG: (keyof Task)[] = [
  'name',
  'description',
  'start',
  'end',
  'status',
  'assignee',
  'priority',
  'completed',
  'type',
  'archived',
];

/**
 * Synchronizes `status` and `completed` fields on a partial task update,
 * respecting custom column categories (`category === 'completed'`).
 */
export function syncStatusAndCompletedFlag(
  originalTask: Task,
  finalUpdates: Partial<Task>,
  customColumns?: Array<{ id: string; category?: string }>
): void {
  if (finalUpdates.status !== undefined) {
    const customCol = customColumns?.find(c => c.id === finalUpdates.status);
    finalUpdates.completed = finalUpdates.status === 'completed' || customCol?.category === 'completed';
  } else if (finalUpdates.completed !== undefined) {
    const currentStatus = originalTask.status || 'open';
    const customCol = customColumns?.find(c => c.id === currentStatus);
    const isCurrentlyCompleted = currentStatus === 'completed' || customCol?.category === 'completed';
    if (finalUpdates.completed && !isCurrentlyCompleted) {
      finalUpdates.status = 'completed';
    } else if (!finalUpdates.completed && isCurrentlyCompleted) {
      finalUpdates.status = 'open';
    }
  }
}

/**
 * Builds human-readable German history entries for changed task fields
 * and merges them with the task's existing history (capped at 100 entries).
 */
export function buildTaskHistoryEntries(
  originalTask: Task,
  updates: Partial<Task>,
  userStr: string,
  fieldsToLog: (keyof Task)[] = DEFAULT_FIELDS_TO_LOG,
  prefixEntries: TaskHistoryEntry[] = []
): TaskHistoryEntry[] {
  const historyEntries: TaskHistoryEntry[] = [...prefixEntries];

  fieldsToLog.forEach(field => {
    if (field in updates) {
      const oldVal = originalTask[field];
      const newVal = updates[field];
      if (oldVal !== newVal) {
        let message = '';

        switch (field) {
          case 'name':
            message = `Titel geändert von "${oldVal || ''}" zu "${newVal || ''}"`;
            break;
          case 'description':
            message = `Beschreibung geändert`;
            break;
          case 'start':
            message = `Startdatum geändert von "${oldVal || 'keins'}" zu "${newVal || 'keins'}"`;
            break;
          case 'end':
            message = `Enddatum geändert von "${oldVal || 'keins'}" zu "${newVal || 'keins'}"`;
            break;
          case 'status':
            message = `Status geändert von "${oldVal || 'offen'}" zu "${newVal || 'offen'}"`;
            break;
          case 'assignee':
            message = `Zuständige Person geändert von "${oldVal || 'keine'}" zu "${newVal || 'keine'}"`;
            break;
          case 'priority':
            message = `Priorität geändert von "${oldVal || 'keine'}" zu "${newVal || 'keine'}"`;
            break;
          case 'completed':
            message = newVal ? 'Aufgabe als erledigt markiert' : 'Aufgabe als offen markiert';
            break;
          case 'type':
            message = `Typ geändert von "${oldVal || 'Aufgabe'}" zu "${newVal || 'Aufgabe'}"`;
            break;
          case 'archived':
            message = newVal ? 'Aufgabe archiviert' : 'Aufgabe wiederhergestellt';
            break;
          default:
            message = `Feld ${String(field)} geändert`;
        }

        historyEntries.push({
          id: Math.random().toString(36).substr(2, 9),
          timestamp: Date.now(),
          user: userStr,
          field,
          oldValue: oldVal ?? null,
          newValue: newVal ?? null,
          message,
        });
      }
    }
  });

  return historyEntries;
}
